import React, { useState, useRef, useEffect } from 'react';
import { Preferences } from '@capacitor/preferences';
import { Button, HeadingRegular, DetailB } from '@pensionbee/honeypot-ui';
import { useBiometrics } from '../biometrics/BiometricsContext';
import BiometricsSetup from '../biometrics/BiometricsSetup';

interface PinSetupProps {
  onSuccess: () => void;
}

const PinSetup = ({ onSuccess }: PinSetupProps) => {
  const [pinDigits, setPinDigits] = useState<string[]>(['', '', '', '']);
  const [confirmPinDigits, setConfirmPinDigits] = useState<string[]>([
    '',
    '',
    '',
    '',
  ]);
  const [pinError, setPinError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [revealIndex, setRevealIndex] = useState<number | null>(null);
  const [revealConfirmIndex, setRevealConfirmIndex] = useState<number | null>(
    null,
  );
  const [step, setStep] = useState<'create' | 'confirm' | 'biometrics'>(
    'create',
  );

  const pinInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const confirmPinInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const { available: biometricAvailable } = useBiometrics();

  useEffect(() => {
    if (step === 'create' && pinDigits.every(Boolean)) {
      setStep('confirm');
      setConfirmPinDigits(['', '', '', '']);
      confirmPinInputRefs.current[0]?.focus();
    }
  }, [pinDigits, step]);

  useEffect(() => {
    if (step === 'create') {
      pinInputRefs.current[0]?.focus();
    } else if (step === 'confirm') {
      confirmPinInputRefs.current[0]?.focus();
    }
  }, [step]);

  const validateConfirmAndSave = async (): Promise<string | null> => {
    const pin = pinDigits.join('');
    const confirmPin = confirmPinDigits.join('');

    if (pin !== confirmPin) {
      return 'Pins do not match.';
    }

    try {
      await Preferences.set({ key: 'PIN_KEY', value: pin });
      return null;
    } catch (err) {
      console.error('Error setting PIN:', err);
      return 'An error occurred while saving your PIN. Please try again.';
    }
  };

  const handleNext = async () => {
    if (isLoading) return;

    setIsLoading(true);
    setPinError('');

    if (step === 'confirm') {
      const error = await validateConfirmAndSave();

      if (error) {
        setPinError(error);
        setConfirmPinDigits(['', '', '', '']);
        confirmPinInputRefs.current[0]?.focus();
        setIsLoading(false);
        return;
      }

      if (biometricAvailable) {
        setStep('biometrics');
        setIsLoading(false);
        return;
      }

      onSuccess();
    }

    setIsLoading(false);
  };

  const handlePinDigitChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>,
    isConfirm: boolean,
  ) => {
    const numeric = e.target.value.replace(/[^0-9]/g, '');

    if (!numeric) {
      if (isConfirm) {
        const next = [...confirmPinDigits];
        next[index] = '';
        setConfirmPinDigits(next);
      } else {
        const next = [...pinDigits];
        next[index] = '';
        setPinDigits(next);
      }
      return;
    }

    const digit = numeric.slice(-1);

    if (isConfirm) {
      const next = [...confirmPinDigits];
      next[index] = digit;
      setConfirmPinDigits(next);

      setRevealConfirmIndex(index);
      setTimeout(() => setRevealConfirmIndex(null), 500);

      if (index < 3) confirmPinInputRefs.current[index + 1]?.focus();
    } else {
      const next = [...pinDigits];
      next[index] = digit;
      setPinDigits(next);

      setRevealIndex(index);
      setTimeout(() => setRevealIndex(null), 500);

      if (index < 3) pinInputRefs.current[index + 1]?.focus();
    }

    setPinError('');
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
    isConfirm: boolean,
  ) => {
    const digits = isConfirm ? confirmPinDigits : pinDigits;
    const refs = isConfirm ? confirmPinInputRefs : pinInputRefs;

    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    isConfirm: boolean,
  ) => {
    e.preventDefault();

    const digits = e.clipboardData
      .getData('text')
      .slice(0, 4)
      .replace(/[^0-9]/g, '')
      .split('');

    if (!digits.length) return;

    if (isConfirm) {
      const next = [...confirmPinDigits];
      digits.forEach((d, i) => (next[i] = d));
      setConfirmPinDigits(next);

      setRevealConfirmIndex(digits.length - 1);
      setTimeout(() => setRevealConfirmIndex(null), 500);

      confirmPinInputRefs.current[Math.min(digits.length, 3)]?.focus();
    } else {
      const next = [...pinDigits];
      digits.forEach((d, i) => (next[i] = d));
      setPinDigits(next);

      setRevealIndex(digits.length - 1);
      setTimeout(() => setRevealIndex(null), 500);

      pinInputRefs.current[Math.min(digits.length, 3)]?.focus();
    }

    setPinError('');
  };

  return step === 'biometrics' ? (
    <BiometricsSetup onSuccess={onSuccess} />
  ) : (
    <form
      onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        handleNext();
      }}
    >
      <div className="mx-auto max-w-header grid grid-cols-12 pt-40 sm:py-32 px-20 sm:px-28">
        {step === 'confirm' && (
          <div className="col-span-2 sm:block mt-12">
            <Button
              className="p-8 [&>svg]:size-24"
              type="button"
              variant="icon"
              onClick={() => {
                setStep('create');
                setPinError('');
                setPinDigits(['', '', '', '']);
                setConfirmPinDigits(['', '', '', '']);
                pinInputRefs.current[0]?.focus();
              }}
              icon="Back"
              aria-label="Go back"
            />
          </div>
        )}
        <div
          className={`${step === 'confirm' ? 'mt-36' : 'mt-96'} col-span-12 md:col-span-6 md:col-start-4`}
        >
          <HeadingRegular className="font-black">
            {step === 'create' ? 'Create a PIN' : 'Confirm PIN'}
          </HeadingRegular>

          <p className="pt-8">
            Make it easy for you to get into your PensionBee account with a PIN.
          </p>

          <div className="pt-28">
            <div className="flex justify-between gap-12 max-w-md">
              {(step === 'create' ? pinDigits : confirmPinDigits).map(
                (digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      if (step === 'create') pinInputRefs.current[index] = el;
                      else confirmPinInputRefs.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    disabled={isLoading}
                    value={
                      step === 'create'
                        ? revealIndex === index
                          ? digit
                          : digit
                            ? '•'
                            : ''
                        : revealConfirmIndex === index
                          ? digit
                          : digit
                            ? '•'
                            : ''
                    }
                    onChange={(e) =>
                      handlePinDigitChange(index, e, step === 'confirm')
                    }
                    onKeyDown={(e) =>
                      handleKeyDown(index, e, step === 'confirm')
                    }
                    onPaste={(e) => handlePaste(e, step === 'confirm')}
                    style={{
                      width: '72px',
                      height: '64px',
                      color: !digit ? 'inherit' : 'var(--color-input)',
                    }}
                    className="text-center text-24 font-bold bg-grey-8 border-2 border-grey-3 rounded-8 focus:outline-none focus:border-blue-100 disabled:opacity-50"
                  />
                ),
              )}
            </div>
            {!!pinError && (
              <div className="mt-[1.75rem] mb-[1.75rem]">
                <div className="bg-red-10 rounded-12 p-20">
                  <DetailB className="text-red-140">
                    PIN doesn&apos;t match, please try again.{' '}
                  </DetailB>
                </div>
              </div>
            )}
          </div>

          {step === 'confirm' && (
            <div className="pt-28">
              <Button className="w-full" type="submit" disabled={isLoading}>
                Next
              </Button>
            </div>
          )}
        </div>
      </div>
    </form>
  );
};

export default PinSetup;
